<template>
  <div class="min-h-screen bg-white dark:bg-slate-950 font-sans relative">

    <RadNavbar
      :is-authenticated="authStore.isAuthenticated"
      :theme="systemStore.theme"
      @sign-in="signInWithProvider('google')" @get-started="signInWithProvider('google')" @go-to-dashboard="router.push('/dashboard')" @sign-out="signOut" @toggle-theme="systemStore.toggleTheme()"
    />

    <!-- Invitation Alert (Rendered elegantly over the hero) -->
    <div v-if="invitationToken" class="absolute top-24 left-1/2 -translate-x-1/2 w-full max-w-md z-40 px-4">
      <div class="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-xl p-6 text-center shadow-2xl">
        <h2 class="text-xl font-bold text-slate-900 dark:text-white mb-2">Account Invitation</h2>
        <p class="text-slate-600 dark:text-slate-300 mb-4 text-sm">You've been invited to join an account.</p>
        <RadButton @click="authStore.isAuthenticated ? acceptInvitation() : signInWithProvider('google')" class="w-full justify-center">
          {{ authStore.isAuthenticated ? 'Accept Invitation' : 'Sign In to Accept' }}
        </RadButton>
      </div>
    </div>

    <RadHero
      title="Build, Deploy & Scale"
      subtitle="Runtime, deployment, and lifecycle-management capabilities for supervised agentic applications. Build and deploy multi-agent applications without the brittle scripts and ad-hoc infrastructure."
      badge-text="Agentic App Platform"
      :primary-cta-text="authStore.isAuthenticated ? 'Go to Dashboard' : 'Request Early Access'"
      @primary-click="authStore.isAuthenticated ? router.push('/dashboard') : signInWithProvider('google')"
    />

    <RadFeatureGrid
      :features="features"
      title="Everything You Need to Build"
      subtitle="Replace brittle scripts and ad-hoc infrastructure with a platform built for the agentic era."
    />

    <RadFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSystemStore } from '@/stores/system'
import { apiFetch } from '@/utils/api'
import {
  RadNavbar,
  RadHero,
  RadFeatureGrid,
  RadFooter,
  RadButton
} from '@radsuite/design-system'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const systemStore = useSystemStore()

const invitationToken = ref<string | null>(null)

onMounted(() => {
  invitationToken.value = route.query.invite as string | null
})

// Feature data explicitly mapped for RadFeatureGrid
const features = [
  { title: 'Context Engineering', description: 'Connect project context and private data sources into agent workflows with controlled access patterns.', icon: 'Database', color: 'red' },
  { title: 'Containerized Isolation', description: 'Run agents in controlled containers with lifecycle management for supervised workflows.', icon: 'ShieldCheck', color: 'blue' },
  { title: 'Multi-Agent Delegation', description: 'Orchestrate complex workflows by delegating tasks between specialized agents with human-in-the-loop approvals.', icon: 'Users', color: 'emerald' },
  { title: 'Real-Time Monitoring', description: 'Live log streaming and resource usage metrics for agentic processes.', icon: 'Activity', color: 'amber' },
  { title: 'Marketplace Distribution', description: 'Publish, monetize, and discover containerized agentic applications as the marketplace comes online.', icon: 'ShoppingBag', color: 'purple', badge: 'Coming Soon' },
  { title: 'Rapid Development', description: 'Build custom agents using integrated project workflows and pre-built toolchains.', icon: 'Zap', color: 'cyan' }
]

async function signInWithProvider(provider: 'google' | 'discord' | 'slack' | 'github') {
  try {
    await authStore.signInWithProvider(provider)
    if (invitationToken.value) {
      await apiFetch('/api/team-invites/accept-token', {
        method: 'POST',
        body: JSON.stringify({ token: invitationToken.value })
      })
    }
    router.push('/dashboard')
  } catch (error) {
    console.error('Sign in failed:', error)
  }
}

async function signOut() {
  try {
    await authStore.signOut()
  } catch (error) {
    console.error('SignOut failed:', error)
  }
}

async function acceptInvitation() {
  if (!invitationToken.value || !authStore.isAuthenticated) return
  try {
    await apiFetch('/api/team-invites/accept-token', {
      method: 'POST',
      body: JSON.stringify({ token: invitationToken.value })
    })
    router.push('/dashboard')
  } catch (error) {
    console.error('Failed to accept invitation:', error)
  }
}

function goToDocs() {
  window.open('https://docs.radsuite.io', '_blank')
}
</script>